import { create } from 'zustand';

import type { ActiveTab, FilterKey, Filters } from '../components/GraphExplorer/GraphExplorer.types';

interface GraphExplorerState {
  // Modal state
  isOpen: boolean;
  centerMmsi: string | null;
  centerVesselName: string | null;

  // Tab state
  activeTab: ActiveTab;

  // Tab 1: Vessel Network filters
  filters: Filters;
  nearbyRadiusKm: number;
  highlightedMmsis: Set<string>;
  filterLoading: boolean;

  // Tab 2: Ownership Intelligence
  selectedFlag: string;
  ownershipFilter: 'all' | 'flagged' | 'multi_flag' | 'shared_addresses';
  showAllOwners: boolean;
  expandFleet: boolean;

  // Tab 3: Sanctions
  expandedProfile: string | null;

  // Actions
  open: (mmsi: string, name: string) => void;
  close: () => void;
  setActiveTab: (tab: ActiveTab) => void;
  toggleFilter: (filter: FilterKey) => void;
  clearFilters: () => void;
  setNearbyRadiusKm: (radius: number) => void;
  setHighlightedMmsis: (mmsis: Set<string>) => void;
  setFilterLoading: (loading: boolean) => void;
  setSelectedFlag: (flag: string) => void;
  setOwnershipFilter: (filter: 'all' | 'flagged' | 'multi_flag' | 'shared_addresses') => void;
  setShowAllOwners: (show: boolean) => void;
  setExpandFleet: (expand: boolean) => void;
  setExpandedProfile: (imo: string | null) => void;
  setCenterVessel: (mmsi: string, name: string) => void;
}

const INITIAL_FILTERS: Filters = {
  nearby: false,
  sameType: false,
  sameDestination: false,
  sameFlag: false,
};

export const useGraphExplorerStore = create<GraphExplorerState>((set) => ({
  // Initial state
  isOpen: false,
  centerMmsi: null,
  centerVesselName: null,
  activeTab: 'vessel-network',
  filters: INITIAL_FILTERS,
  nearbyRadiusKm: 50,
  highlightedMmsis: new Set(),
  filterLoading: false,
  selectedFlag: '',
  ownershipFilter: 'all',
  showAllOwners: false,
  expandFleet: false,
  expandedProfile: null,

  // Actions
  open: (mmsi, name) =>
    set({
      isOpen: true,
      centerMmsi: mmsi,
      centerVesselName: name,
      activeTab: 'vessel-network',
      filters: INITIAL_FILTERS,
      highlightedMmsis: new Set(),
      selectedFlag: '',
      expandedProfile: null,
    }),

  close: () =>
    set({
      isOpen: false,
      centerMmsi: null,
      centerVesselName: null,
      highlightedMmsis: new Set(),
      selectedFlag: '',
      expandedProfile: null,
    }),

  setActiveTab: (tab) =>
    set((state) => ({
      activeTab: tab,
      highlightedMmsis: new Set(),
      filters: INITIAL_FILTERS,
      selectedFlag: tab !== 'ownership' ? '' : state.selectedFlag,
    })),

  toggleFilter: (filter) =>
    set((state) => ({
      filters: { ...state.filters, [filter]: !state.filters[filter] },
    })),

  clearFilters: () => set({ filters: INITIAL_FILTERS, highlightedMmsis: new Set() }),

  setNearbyRadiusKm: (radius) => set({ nearbyRadiusKm: radius }),

  setHighlightedMmsis: (mmsis) => set({ highlightedMmsis: mmsis }),

  setFilterLoading: (loading) => set({ filterLoading: loading }),

  setSelectedFlag: (flag) => set({ selectedFlag: flag, ownershipFilter: 'all' }),

  setOwnershipFilter: (filter) => set({ ownershipFilter: filter }),

  setShowAllOwners: (show) => set({ showAllOwners: show }),

  setExpandFleet: (expand) => set({ expandFleet: expand }),

  setExpandedProfile: (imo) => set({ expandedProfile: imo }),

  setCenterVessel: (mmsi, name) =>
    set({
      centerMmsi: mmsi,
      centerVesselName: name,
      filters: INITIAL_FILTERS,
      highlightedMmsis: new Set(),
    }),
}));
